import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Sparkles, Heart, Users, Leaf, CreditCard, AlertCircle, ShieldAlert, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import { useState, useRef, useCallback, useEffect } from "react";

const TIMMA_URL = "https://bestill.timma.no/beautybykuleva";
const WEDDING_IMAGE = "https://customer-assets.emergentagent.com/job_141dc66c-4a99-4f53-829b-56669273ddfe/artifacts/xkvnbfa9_IMG_6825.jpeg";

// Wedding Gallery Media
const weddingMedia = [
  {
    id: "wedding-photo-1",
    type: "image",
    url: "https://customer-assets.emergentagent.com/job_141dc66c-4a99-4f53-829b-56669273ddfe/artifacts/r2cu41cd_IMG_7935.jpeg",
  },
  {
    id: "wedding-video-1",
    type: "video",
    url: "https://customer-assets.emergentagent.com/job_141dc66c-4a99-4f53-829b-56669273ddfe/artifacts/3jbe3w2r_instories_A607A247-75A1-408C-885C-1AB265C4BA9B.mov",
  },
  {
    id: "wedding-photo-2",
    type: "image",
    url: "https://customer-assets.emergentagent.com/job_141dc66c-4a99-4f53-829b-56669273ddfe/artifacts/7l4vkef6_IMG_8690.jpeg",
  },
  {
    id: "wedding-video-2",
    type: "video",
    url: "https://customer-assets.emergentagent.com/job_141dc66c-4a99-4f53-829b-56669273ddfe/artifacts/q9tinvt3_video-output-CA32DD8D-83E0-4755-ABB7-571EA3AE86DF.mov",
  },
];

function SectionDivider() {
  return (
    <div className="flex items-center justify-center my-10 sm:my-14">
      <div className="h-px bg-gradient-to-r from-transparent via-[#E8D0D0] to-transparent w-full max-w-md" />
    </div>
  );
}

// Video Thumbnail Component for Wedding Gallery
function WeddingVideoThumbnail({ item, onClick }) {
  const videoRef = useRef(null);
  const [thumbnailReady, setThumbnailReady] = useState(false);
  
  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.min(1.5, videoRef.current.duration - 0.1);
    }
  }, []);

  const handleSeeked = useCallback(() => {
    setThumbnailReady(true);
  }, []);

  return (
    <div
      className="relative group cursor-pointer overflow-hidden rounded-2xl shadow-lg shadow-[#D4A5A5]/10 hover:shadow-xl transition-all duration-300"
      onClick={onClick}
    >
      <div className="w-full aspect-[4/5] bg-[#F9F5F6] relative overflow-hidden">
        <video
          ref={videoRef}
          src={item.url}
          className={`w-full h-full object-cover transition-opacity duration-300 ${thumbnailReady ? 'opacity-100' : 'opacity-0'}`}
          muted
          playsInline
          preload="metadata"
          onLoadedMetadata={handleLoadedMetadata}
          onSeeked={handleSeeked}
        />
        {!thumbnailReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#F9F5F6]">
            <div className="w-8 h-8 border-3 border-[#D4A5A5]/30 border-t-[#D4A5A5] rounded-full animate-spin" />
          </div>
        )}
      </div>
      
      {/* Play Icon Overlay */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
          <Play size={24} className="text-[#D4A5A5] ml-1" fill="#D4A5A5" />
        </div>
      </div>
      
      {/* Video Badge */}
      <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm flex items-center gap-1.5">
        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
        <span className="text-white text-xs font-medium">Video</span>
      </div>
    </div>
  );
}

// Video Lightbox for Wedding Gallery
function WeddingVideoLightbox({ item, onClose }) {
  const videoRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleLoadedData = useCallback(() => {
    setIsLoading(false);
    if (videoRef.current) {
      videoRef.current.play().catch(console.error);
    }
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: "spring", damping: 25 }}
        className="relative w-full max-w-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 z-10 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors"
          aria-label="Close"
        >
          <span className="text-2xl">×</span>
        </button>

        <div className="relative rounded-2xl overflow-hidden bg-black">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black z-10">
              <div className="w-12 h-12 border-4 border-[#D4A5A5]/30 border-t-[#D4A5A5] rounded-full animate-spin" />
            </div>
          )}
          
          <video
            ref={videoRef}
            src={item.url}
            className="w-full max-h-[75vh] object-contain"
            controls
            playsInline
            onLoadedData={handleLoadedData}
          />
        </div>
      </motion.div>
    </motion.div>
  );
}

// Image Lightbox for Wedding Gallery
function WeddingImageLightbox({ item, onClose }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: "spring", damping: 25 }}
        className="relative max-w-5xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 z-10 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors"
          aria-label="Close"
        >
          <span className="text-2xl">×</span>
        </button>

        <div className="relative rounded-2xl overflow-hidden">
          <img
            src={item.url}
            alt="Wedding"
            className="w-full max-h-[85vh] object-contain"
          />
        </div>
      </motion.div>
    </motion.div>
  );
}

function InfoSection({ icon: Icon, title, children, testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg shadow-[#D4A5A5]/10 border border-[#F9F5F6]"
      data-testid={testId}
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-[#F9F5F6] to-[#F2E8E8] flex items-center justify-center text-[#D4A5A5]">
          <Icon size={20} className="sm:w-6 sm:h-6" />
        </div>
        <h3 className="font-['Playfair_Display'] text-xl sm:text-2xl font-bold text-[#4A4A4A]">
          {title}
        </h3>
      </div>
      {children}
    </motion.div>
  );
}

export default function WeddingPage() {
  const { t } = useLanguage();
  const [selectedMedia, setSelectedMedia] = useState(null);

  return (
    <div className="pt-20 bg-[#FDFBFB]" data-testid="wedding-page">
      {/* Hero Section */}
      <section className="relative py-16 sm:py-24 bg-gradient-to-b from-[#F9F5F6] to-[#FDFBFB] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center lg:text-left"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 text-[#D4A5A5] text-sm font-medium mb-6 shadow-sm">
                <Sparkles size={16} />
                Beauty by Kuleva
              </span>
              <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[#4A4A4A] mb-4">
                {t("wedding.title")}
              </h1>
              <p className="text-[#D4A5A5] text-lg sm:text-xl font-medium italic mb-6">
                {t("wedding.subtitle")}
              </p>
              <p className="text-[#666666] text-base sm:text-lg leading-relaxed mb-4">
                {t("wedding.intro1")}
              </p>
              <p className="text-[#666666] text-base sm:text-lg leading-relaxed mb-8">
                {t("wedding.intro2")}
              </p>
              
              {/* Main Price */}
              <div className="bg-gradient-to-r from-[#F9F5F6] to-white rounded-2xl p-5 sm:p-6 border border-[#E8D0D0] mb-8">
                <p className="text-[#4A4A4A] text-lg sm:text-xl font-bold">
                  {t("wedding.mainPrice")}
                </p>
              </div>

              {/* Book Button */}
              <a
                href={TIMMA_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="wedding-hero-book-btn"
              >
                <Button className="btn-primary rounded-full px-8 sm:px-10 py-5 sm:py-6 text-base shadow-lg hover:shadow-xl transition-all">
                  {t("wedding.bookButton")}
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </a>
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-[#D4A5A5]/20">
                <img
                  src={WEDDING_IMAGE}
                  alt="Bridal makeup"
                  className="w-full h-[400px] sm:h-[500px] lg:h-[600px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 sm:w-32 sm:h-32 bg-[#F9F5F6] rounded-full -z-10" />
              <div className="absolute -top-4 -left-4 w-16 h-16 sm:w-20 sm:h-20 bg-[#D4A5A5]/20 rounded-full -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Wedding Gallery Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#4A4A4A] mb-3">
              Mitt arbeid
            </h2>
            <p className="text-[#8C8C8C]">Se eksempler på brudestyling</p>
          </motion.div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {weddingMedia.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                {item.type === "video" ? (
                  <WeddingVideoThumbnail
                    item={item}
                    onClick={() => setSelectedMedia(item)}
                  />
                ) : (
                  <div
                    className="relative group cursor-pointer overflow-hidden rounded-2xl shadow-lg shadow-[#D4A5A5]/10 hover:shadow-xl transition-all duration-300"
                    onClick={() => setSelectedMedia(item)}
                  >
                    <img
                      src={item.url}
                      alt="Brudestyling"
                      className="w-full aspect-[4/5] object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Media Lightbox */}
      <AnimatePresence>
        {selectedMedia && (
          selectedMedia.type === "video" ? (
            <WeddingVideoLightbox
              item={selectedMedia}
              onClose={() => setSelectedMedia(null)}
            />
          ) : (
            <WeddingImageLightbox
              item={selectedMedia}
              onClose={() => setSelectedMedia(null)}
            />
          )
        )}
      </AnimatePresence>

      {/* Content Sections */}
      <section className="py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          
          {/* Trial Session */}
          <InfoSection icon={Sparkles} title={t("wedding.trialTitle")} testId="wedding-trial-section">
            <p className="text-[#666666] mb-4 leading-relaxed">
              {t("wedding.trialDescription")}
            </p>
            <p className="text-[#4A4A4A] font-bold mb-5">{t("wedding.trialPrice")}</p>
            
            <p className="text-[#4A4A4A] font-semibold mb-3">{t("wedding.trialHeading")}</p>
            <ul className="space-y-2 mb-5">
              <li className="flex items-start gap-2 text-[#666666]">
                <span className="text-[#D4A5A5] mt-1.5">•</span>
                {t("wedding.trialItem1")}
              </li>
              <li className="flex items-start gap-2 text-[#666666]">
                <span className="text-[#D4A5A5] mt-1.5">•</span>
                {t("wedding.trialItem2")}
              </li>
              <li className="flex items-start gap-2 text-[#666666]">
                <span className="text-[#D4A5A5] mt-1.5">•</span>
                {t("wedding.trialItem3")}
              </li>
            </ul>
            <p className="text-[#666666] italic">
              {t("wedding.trialConclusion")}
            </p>
          </InfoSection>

          <SectionDivider />

          {/* Guests Styling */}
          <InfoSection icon={Users} title={t("wedding.guestsTitle")} testId="wedding-guests-section">
            <p className="text-[#666666] mb-4">{t("wedding.guestsDescription")}</p>
            <ul className="space-y-2 mb-5">
              <li className="flex items-start gap-2 text-[#666666]">
                <span className="text-[#D4A5A5] mt-1.5">•</span>
                {t("wedding.guestsItem1")}
              </li>
              <li className="flex items-start gap-2 text-[#666666]">
                <span className="text-[#D4A5A5] mt-1.5">•</span>
                {t("wedding.guestsItem2")}
              </li>
              <li className="flex items-start gap-2 text-[#666666]">
                <span className="text-[#D4A5A5] mt-1.5">•</span>
                {t("wedding.guestsItem3")}
              </li>
            </ul>
            <p className="text-[#4A4A4A] font-bold mb-4">{t("wedding.guestsPrice")}</p>
            <div className="bg-[#F9F5F6] rounded-xl p-4">
              <p className="text-[#666666] text-sm">{t("wedding.travelNote")}</p>
              <p className="text-[#4A4A4A] font-semibold mt-2">{t("wedding.travelPrice")}</p>
            </div>
          </InfoSection>

          <SectionDivider />

          {/* Preparation */}
          <InfoSection icon={Leaf} title={t("wedding.prepTitle")} testId="wedding-prep-section">
            <p className="text-[#666666] mb-4">{t("wedding.prepDescription")}</p>
            <ul className="space-y-2 mb-5">
              <li className="flex items-start gap-2 text-[#666666]">
                <span className="text-[#D4A5A5] mt-1.5">•</span>
                {t("wedding.prepItem1")}
              </li>
              <li className="flex items-start gap-2 text-[#666666]">
                <span className="text-[#D4A5A5] mt-1.5">•</span>
                {t("wedding.prepItem2")}
              </li>
              <li className="flex items-start gap-2 text-[#666666]">
                <span className="text-[#D4A5A5] mt-1.5">•</span>
                {t("wedding.prepItem3")}
              </li>
              <li className="flex items-start gap-2 text-[#666666]">
                <span className="text-[#D4A5A5] mt-1.5">•</span>
                {t("wedding.prepItem4")}
              </li>
            </ul>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-amber-800 text-sm font-medium">
                {t("wedding.prepWarning")}
              </p>
            </div>
          </InfoSection>

          <SectionDivider />

          {/* Booking */}
          <InfoSection icon={CreditCard} title={t("wedding.bookingTitle")} testId="wedding-booking-section">
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-[#666666]">
                <span className="text-[#D4A5A5] mt-1.5">•</span>
                {t("wedding.bookingItem1")}
              </li>
              <li className="flex items-start gap-2 text-[#666666]">
                <span className="text-[#D4A5A5] mt-1.5">•</span>
                {t("wedding.bookingItem2")}
              </li>
            </ul>
          </InfoSection>

          <SectionDivider />

          {/* Cancellation */}
          <InfoSection icon={AlertCircle} title={t("wedding.cancellationTitle")} testId="wedding-cancellation-section">
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-[#666666]">
                <span className="text-[#D4A5A5] mt-1.5">•</span>
                {t("wedding.cancellationItem1")}
              </li>
              <li className="flex items-start gap-2 text-[#666666]">
                <span className="text-[#D4A5A5] mt-1.5">•</span>
                {t("wedding.cancellationItem2")}
              </li>
              <li className="flex items-start gap-2 text-[#666666]">
                <span className="text-[#D4A5A5] mt-1.5">•</span>
                {t("wedding.cancellationItem3")}
              </li>
            </ul>
          </InfoSection>

          <SectionDivider />

          {/* Force Majeure */}
          <InfoSection icon={ShieldAlert} title={t("wedding.forceTitle")} testId="wedding-force-section">
            <p className="text-[#666666] mb-4 leading-relaxed">
              {t("wedding.forceItem1")}
            </p>
            <p className="text-[#666666] leading-relaxed">
              {t("wedding.forceItem2")}
            </p>
          </InfoSection>

        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-t from-[#F9F5F6] to-[#FDFBFB]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Heart className="w-12 h-12 mx-auto mb-6 text-[#D4A5A5]" />
            <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl lg:text-4xl font-bold text-[#4A4A4A] mb-8">
              {t("wedding.bookButton")}
            </h2>
            <a
              href={TIMMA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="wedding-cta-book-btn"
            >
              <Button className="btn-primary rounded-full px-10 py-6 text-base shadow-lg hover:shadow-xl transition-shadow">
                {t("wedding.bookButton")}
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
